import type { Call, CallContactNameState, CallContactNameUpdateResult } from '@bcio-voice/contracts';
import { useEffect, useRef, useSyncExternalStore } from 'react';
import { Check, Pencil, X } from 'lucide-react';
import { Icon } from '../../ui/Icon.tsx';
import { type ApiClient, type DataEnvelope, isAbortError, isApiRequestError } from '../../lib/api.ts';
import { hasPermission } from '../../lib/capabilities.ts';
import { cleanText } from '../phone/contextCard.ts';
import { useWorkspace } from '../../state/workspace.tsx';

const PLACEHOLDER_PATTERN = /^(?:unnamed contact|not in pipeline|pipeline contact|unknown(?: contact)?|unknown caller(?:\s*\(number withheld\))?)$/i;
const PHONE_LIKE_PLACEHOLDER = /^\+?[\d()\s.-]{7,25}$/;

export function isPhoneLikeDisplayName(value: string): boolean {
  const t = value.trim();
  if (!PHONE_LIKE_PLACEHOLDER.test(t)) return false;
  const digits = t.replace(/\D/g, '');
  return digits.length >= 7 && digits.length <= 15;
}

export function isPlaceholderDisplayName(value: string | null | undefined): boolean {
  const t = (value ?? '').trim();
  if (!t || !/[\p{L}\p{N}]/u.test(t)) return true;
  if (PLACEHOLDER_PATTERN.test(t)) return true;
  return isPhoneLikeDisplayName(t);
}

export function validateContactNameDraft(raw: string): { ok: true; value: string } | { ok: false; message: string } {
  const normalized = raw.replace(/\s+/g, ' ').trim();
  if (!normalized) {
    return { ok: false, message: 'Enter a contact name before saving.' };
  }
  if (normalized.length > 200) {
    return { ok: false, message: 'Contact name must be 200 characters or fewer.' };
  }
  if (/[\u0000-\u001f\u007f<>]/.test(normalized) || !/[\p{L}\p{N}]/u.test(normalized) || PLACEHOLDER_PATTERN.test(normalized) || isPhoneLikeDisplayName(normalized)) {
    return { ok: false, message: 'Enter a real contact name (plain text, not a placeholder or phone number).' };
  }
  return { ok: true, value: normalized };
}

function makeIdempotencyKey(callId: string): string {
  const rand = typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
    ? crypto.randomUUID()
    : `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 12)}`;
  return `cname-${callId.slice(0, 8)}-${rand}`.slice(0, 120);
}

export interface ContactNameEditorContext {
  api: ApiClient;
  call: Call;
  displayedName: string;
  tenantId: string;
  userId: string;
  permissions: readonly string[];
  viewAs: boolean;
  onUpsertCall: (call: Call) => void;
}

export interface ContactNameEditorSnapshot {
  scopeKey: string;
  headingText: string;
  editing: boolean;
  draft: string;
  saving: boolean;
  errorMsg: string | null;
  remoteState: CallContactNameState | null;
  loadingState: boolean;
  canRead: boolean;
  canWrite: boolean;
  isInternal: boolean;
  canStartEdit: boolean;
  blockedReason: CallContactNameState['reason'];
}

export interface FocusableInput {
  focus: () => void;
  select: () => void;
}

export interface KeyEventLike {
  key: string;
  preventDefault: () => void;
  stopPropagation: () => void;
}

export class ContactNameEditorController {
  private ctx: ContactNameEditorContext;
  private scopeKey: string;
  private remoteState: CallContactNameState | null = null;
  private loadingState = false;
  private editing = false;
  private draft = '';
  private saving = false;
  private errorMsg: string | null = null;
  private savedName: string | null = null;
  private attemptKey: { fingerprint: string; key: string } | null = null;
  private abortCtrl: AbortController | null = null;
  private inputEl: FocusableInput | null = null;
  private needsFocusSelect = false;
  private readonly listeners = new Set<() => void>();
  private snapshotCache: ContactNameEditorSnapshot;

  constructor(ctx: ContactNameEditorContext) {
    this.ctx = ctx;
    this.scopeKey = ContactNameEditorController.scopeFor(ctx);
    this.snapshotCache = this.computeSnapshot();
  }

  static scopeFor(ctx: Pick<ContactNameEditorContext, 'tenantId' | 'userId' | 'call' | 'viewAs'>): string {
    return `${ctx.tenantId || 'tenant'}:${ctx.userId}:${ctx.call.id}:${ctx.viewAs ? 'view-as' : 'self'}`;
  }

  subscribe = (listener: () => void): (() => void) => {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  };

  getSnapshot = (): ContactNameEditorSnapshot => this.snapshotCache;

  private emit(): void {
    this.snapshotCache = this.computeSnapshot();
    for (const listener of this.listeners) listener();
  }

  syncContext(next: ContactNameEditorContext): boolean {
    const nextScope = ContactNameEditorController.scopeFor(next);
    const scopeChanged = nextScope !== this.scopeKey;
    const prevDisplay = this.ctx.displayedName;
    const prevContactName = this.ctx.call.contact_display_name;
    const prevPerms = this.ctx.permissions;
    this.ctx = next;
    if (scopeChanged) {
      this.abortCtrl?.abort();
      this.abortCtrl = null;
      this.scopeKey = nextScope;
      this.remoteState = null;
      this.loadingState = false;
      this.editing = false;
      this.draft = '';
      this.saving = false;
      this.errorMsg = null;
      this.savedName = null;
      this.attemptKey = null;
      this.needsFocusSelect = false;
      this.emit();
      return true;
    }
    if (prevDisplay !== next.displayedName || prevContactName !== next.call.contact_display_name || prevPerms !== next.permissions) {
      this.emit();
    }
    return false;
  }

  dispose(): void {
    this.abortCtrl?.abort();
    this.abortCtrl = null;
  }

  attachInput = (el: FocusableInput | null): void => {
    this.inputEl = el;
    if (el && this.editing && this.needsFocusSelect) {
      this.needsFocusSelect = false;
      el.focus();
      el.select();
    }
  };

  private computeSnapshot(): ContactNameEditorSnapshot {
    const { call, displayedName, permissions, viewAs } = this.ctx;
    const canRead = hasPermission(permissions, 'contacts:read');
    const canWrite = !viewAs && hasPermission(permissions, 'contacts:write');
    const isInternal = call.direction === 'internal' || call.destination.kind === 'internal';
    const verifiedFromCall = cleanText(call.contact_display_name);
    const verifiedFromRemote = this.remoteState && !this.remoteState.is_placeholder && this.remoteState.display_name
      ? cleanText(this.remoteState.display_name)
      : '';
    const headingText = this.savedName || verifiedFromCall || verifiedFromRemote || displayedName;
    const canStartEdit = canWrite && !isInternal && (this.remoteState ? this.remoteState.editable : true);
    const blockedReason: CallContactNameState['reason'] = !canWrite || isInternal
      ? (viewAs ? 'view_as_read_only' : !canWrite ? 'missing_permission' : 'internal_call')
      : this.remoteState && !this.remoteState.editable
        ? this.remoteState.reason
        : null;

    return {
      scopeKey: this.scopeKey,
      headingText,
      editing: this.editing,
      draft: this.draft,
      saving: this.saving,
      errorMsg: this.errorMsg,
      remoteState: this.remoteState,
      loadingState: this.loadingState,
      canRead,
      canWrite,
      isInternal,
      canStartEdit,
      blockedReason,
    };
  }

  async fetchState(signal?: AbortSignal): Promise<CallContactNameState | null> {
    const snap = this.snapshotCache;
    if (!snap.canRead || snap.isInternal) return null;
    const activeScope = this.scopeKey;
    const callId = this.ctx.call.id;
    this.loadingState = true;
    this.emit();
    try {
      const res = await this.ctx.api.request<DataEnvelope<CallContactNameState>>(
        `/calls/${encodeURIComponent(callId)}/contact-name`,
        { signal: signal ?? null },
      );
      if (this.scopeKey !== activeScope) return null;
      this.remoteState = res?.data ?? null;
      this.loadingState = false;
      this.emit();
      return this.remoteState;
    } catch (err) {
      if (isAbortError(err) || this.scopeKey !== activeScope) return null;
      this.loadingState = false;
      this.emit();
      return null;
    }
  }

  startEditing = (): void => {
    const snap = this.computeSnapshot();
    if (!snap.canWrite || snap.isInternal || this.editing) return;
    if (this.remoteState && !this.remoteState.editable) {
      if (this.remoteState.reason === 'ambiguous_contact_match') {
        this.errorMsg = 'Multiple contacts match this phone number. Resolve the duplicate in Contacts before editing.';
      } else if (this.remoteState.reason === 'crm_unavailable') {
        this.errorMsg = 'Pipeline CRM contact service is temporarily unavailable.';
      }
      this.emit();
      return;
    }
    this.errorMsg = null;
    this.draft = snap.headingText;
    this.editing = true;
    this.needsFocusSelect = true;
    this.emit();
    if (this.inputEl && this.needsFocusSelect) {
      this.needsFocusSelect = false;
      this.inputEl.focus();
      this.inputEl.select();
    }
  };

  setDraft = (value: string): void => {
    if (!this.editing) return;
    this.draft = value;
    if (this.errorMsg) this.errorMsg = null;
    this.emit();
  };

  cancelEditing = (): void => {
    if (this.saving) {
      this.abortCtrl?.abort();
      this.abortCtrl = null;
      this.saving = false;
    }
    this.attemptKey = null;
    this.errorMsg = null;
    this.editing = false;
    this.needsFocusSelect = false;
    this.emit();
  };

  /** Blur intentionally does NOT save or discard the user's draft. */
  handleBlur = (): void => {
    // No-op by design: only Enter or Save commits, and only Escape or Cancel discards.
  };

  handleKeyDown = (e: KeyEventLike): void => {
    e.stopPropagation();
    if (e.key === 'Enter') {
      e.preventDefault();
      void this.commitSave();
    } else if (e.key === 'Escape') {
      e.preventDefault();
      this.cancelEditing();
    }
  };

  handleKeyUp = (e: Pick<KeyEventLike, 'stopPropagation'>): void => {
    e.stopPropagation();
  };

  commitSave = async (): Promise<boolean> => {
    const snap = this.computeSnapshot();
    if (this.saving || !this.editing || !snap.canWrite || snap.isInternal) return false;
    const check = validateContactNameDraft(this.draft);
    if (!check.ok) {
      this.errorMsg = check.message;
      this.emit();
      return false;
    }

    const activeScope = this.scopeKey;
    const callId = this.ctx.call.id;
    this.saving = true;
    this.errorMsg = null;
    this.emit();

    const ctrl = new AbortController();
    this.abortCtrl = ctrl;

    try {
      let currentTarget = this.remoteState;
      if (!currentTarget || !currentTarget.expected_version) {
        currentTarget = await this.fetchState(ctrl.signal);
        if (this.scopeKey !== activeScope) return false;
      }
      if (!currentTarget || !currentTarget.editable || !currentTarget.expected_version) {
        this.errorMsg = currentTarget?.reason === 'ambiguous_contact_match'
          ? 'Multiple contacts match this phone number. Resolve the duplicate in Contacts before editing.'
          : currentTarget?.reason === 'view_as_read_only'
            ? 'View-as sessions are read-only.'
            : currentTarget?.reason === 'missing_permission'
              ? 'You do not have permission to edit contacts.'
              : 'Unable to load the current contact version. Try saving again.';
        return false;
      }

      const expectedVersion = currentTarget.expected_version;
      const fingerprint = `${activeScope}:${expectedVersion}:${check.value}`;
      if (!this.attemptKey || this.attemptKey.fingerprint !== fingerprint) {
        this.attemptKey = { fingerprint, key: makeIdempotencyKey(callId) };
      }
      const idempotencyKey = this.attemptKey.key;

      const res = await this.ctx.api.request<DataEnvelope<CallContactNameUpdateResult>>(
        `/calls/${encodeURIComponent(callId)}/contact-name`,
        {
          method: 'PATCH',
          idempotencyKey,
          signal: ctrl.signal,
          body: JSON.stringify({
            display_name: check.value,
            expected_version: expectedVersion,
          }),
        },
      );
      if (this.scopeKey !== activeScope) return false;

      const result = res.data;
      this.attemptKey = null;
      this.remoteState = result.state;
      this.savedName = result.contact.display_name;
      this.errorMsg = null;
      this.editing = false;
      this.needsFocusSelect = false;
      this.ctx.onUpsertCall(result.call);
      return true;
    } catch (err) {
      if (isAbortError(err) || this.scopeKey !== activeScope) return false;
      this.attemptKey = null;
      if (isApiRequestError(err)) {
        this.errorMsg = err.message || 'Could not save the contact name.';
        if (err.status === 409) {
          void this.fetchState();
        }
      } else {
        this.errorMsg = 'Could not save the contact name. Check your connection and try again.';
      }
      return false;
    } finally {
      if (this.scopeKey === activeScope) {
        this.saving = false;
        this.emit();
      }
    }
  };
}

export function CallContactNameEditor({
  call,
  displayedName,
  controller: externalController,
}: {
  call: Call;
  displayedName: string;
  controller?: ContactNameEditorController;
}) {
  const ws = useWorkspace();
  const viewAs = Boolean(ws.session?.view_as || ws.config.value?.view_as);
  const tenantId = ws.session?.tenant?.id ?? 'tenant';

  const ctx: ContactNameEditorContext = {
    api: ws.api,
    call,
    displayedName,
    tenantId,
    userId: ws.userId,
    permissions: ws.permissions,
    viewAs,
    onUpsertCall: ws.upsert,
  };

  const internalRef = useRef<ContactNameEditorController | null>(null);
  if (!internalRef.current && !externalController) {
    internalRef.current = new ContactNameEditorController(ctx);
  }
  const ctrl = externalController ?? internalRef.current!;
  ctrl.syncContext(ctx);

  const snap = useSyncExternalStore(ctrl.subscribe, ctrl.getSnapshot, ctrl.getSnapshot);

  useEffect(() => {
    if (!snap.canRead || snap.isInternal) return;
    const abort = new AbortController();
    void ctrl.fetchState(abort.signal);
    return () => abort.abort();
  }, [ctrl, snap.canRead, snap.isInternal, snap.scopeKey]);

  useEffect(() => {
    return () => {
      if (!externalController) ctrl.dispose();
    };
  }, [ctrl, externalController]);

  return (
    <div className="detail-title-wrap">
      <div className="detail-title-row" style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
        <h2
          id="call-detail-title"
          className="detail-title"
          onDoubleClick={snap.canWrite && !snap.isInternal && !snap.editing ? ctrl.startEditing : undefined}
          title={snap.canStartEdit && !snap.editing ? 'Double-click to edit contact name' : undefined}
          style={{ margin: 0 }}
        >
          {snap.editing ? (
            <span className="detail-title-editor" style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', flexWrap: 'wrap' }}>
              <input
                ref={ctrl.attachInput}
                type="text"
                className="input detail-title-input"
                aria-label="Contact name"
                value={snap.draft}
                maxLength={200}
                disabled={snap.saving}
                onChange={e => ctrl.setDraft(e.target.value)}
                onBlur={ctrl.handleBlur}
                onKeyDown={ctrl.handleKeyDown}
                onKeyUp={ctrl.handleKeyUp}
              />
              <button
                type="button"
                className="btn btn-primary btn-sm"
                aria-label="Save contact name"
                disabled={snap.saving}
                onClick={() => void ctrl.commitSave()}
              >
                <Icon icon={Check} /> <span>{snap.saving ? 'Saving…' : 'Save'}</span>
              </button>
              <button
                type="button"
                className="btn btn-quiet btn-sm"
                aria-label="Cancel editing contact name"
                disabled={snap.saving}
                onClick={ctrl.cancelEditing}
              >
                <Icon icon={X} /> <span>Cancel</span>
              </button>
            </span>
          ) : (
            snap.headingText
          )}
        </h2>
        {!snap.editing && snap.canStartEdit ? (
          <button
            type="button"
            className="btn btn-quiet btn-sm detail-title-edit-btn"
            aria-label="Edit contact name"
            title="Edit contact name"
            onClick={ctrl.startEditing}
          >
            <Icon icon={Pencil} />
          </button>
        ) : null}
      </div>
      {snap.errorMsg ? (
        <p className="field-error detail-title-error" role="alert" style={{ marginTop: '4px', marginBottom: 0 }}>
          {snap.errorMsg}
        </p>
      ) : snap.blockedReason === 'ambiguous_contact_match' ? (
        <p className="field-hint detail-title-hint" style={{ marginTop: '4px', marginBottom: 0 }}>
          Multiple contacts match this number. Resolve duplicates in Contacts to edit.
        </p>
      ) : null}
    </div>
  );
}
